import { useProductDetail } from '@/contexts/ProductDetailContext';
import { useIsMobile } from '@/hooks/use-mobile';
import { AlertCircle, Heart, Star, X } from 'lucide-react';
import { 
  Drawer, 
  DrawerContent, 
  DrawerDescription, 
  DrawerHeader, 
  DrawerTitle 
} from './ui/drawer';
import { 
  Sheet, 
  SheetContent, 
  SheetDescription, 
  SheetHeader, 
  SheetTitle 
} from './ui/sheet';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { useCartStore } from '@/store/cartStore';
import { useFavoritesStore } from '@/store/favoritesStore';
import { FEATURED_PRODUCTS } from '@/data/products';
import { useNavigate, useParams } from 'react-router-dom';

export function ProductDetailSheet() {
  const isMobile = useIsMobile();
  const { isOpen, currentProduct, closeProductDetail } = useProductDetail();
  const [activeTab, setActiveTab] = useState<'description' | 'effects' | 'usage'>('description');
  const { addItem } = useCartStore();
  const { isFavorite, addFavorite, removeFavorite } = useFavoritesStore();
  const navigate = useNavigate();
  const { id: urlId } = useParams();
  
  // This effect loads the product from the URL if it's not already loaded
  useEffect(() => {
    if (urlId && !currentProduct) {
      const productFromUrl = FEATURED_PRODUCTS.find(p => p.id === urlId);
      if (productFromUrl) {
        // Update the URL to match the product but don't close or re-open the sheet
        navigate(`/product/${productFromUrl.id}`, { replace: true });
      }
    }
  }, [urlId, currentProduct, navigate]);
  
  if (!currentProduct) return null;
  
  const favorite = currentProduct.id ? isFavorite(currentProduct.id) : false;
  
  const handleAddToCart = () => {
    if (currentProduct.prescription) {
      toast.error("This medication requires a prescription. Please upload one before purchasing.");
      return;
    }

    addItem({
      id: currentProduct.id,
      title: currentProduct.title,
      price: currentProduct.price,
      image: currentProduct.image,
      category: currentProduct.category,
      prescription: currentProduct.prescription,
    });
    
    toast.success(`${currentProduct.title} has been added to your medication tray`);
  };

  const handleToggleFavorite = () => {
    if (favorite) {
      removeFavorite(currentProduct.id);
      toast.success(`${currentProduct.title} has been removed from your favorites`);
    } else {
      addFavorite(currentProduct);
      toast.success(`${currentProduct.title} has been added to your favorites`);
    }
  };

  const handleViewFullDetails = () => {
    // Update URL for SEO but don't close the sheet
    // This enables bookmarking and sharing the URL
    navigate(`/product/${currentProduct.id}`, { replace: true });
  };
  
  const getProductDescription = () => {
    // Simulated product descriptions based on category
    const descriptions: Record<string, string> = {
      "Pain Relief": "This medication provides fast and effective relief from pain and inflammation. It works by blocking the production of certain natural substances that cause inflammation, thereby reducing pain and swelling.",
      "Antibiotics": "This antibiotic belongs to a class of drugs known as macrolides. It works by stopping the growth of bacteria. This medication will not work for viral infections (such as common cold, flu).",
      "Diabetes": "This medication helps control blood sugar levels in people with type 2 diabetes. It works by decreasing the amount of sugar your liver makes and helping your body respond better to the insulin it produces naturally.",
      "Heart Health": "This medication is used to treat high blood pressure (hypertension) and chest pain (angina). It works by relaxing blood vessels so blood can flow more easily.",
      "Vitamins": "This supplement provides essential nutrients that may be missing from your diet. It helps maintain overall health and wellbeing by supporting various bodily functions.",
      "Respiratory": "This medication is used to prevent or treat wheezing, difficulty breathing, chest tightness, and coughing caused by certain breathing problems. It works by relaxing muscles in the airways to improve breathing."
    };
    
    return descriptions[currentProduct.category] || "No detailed description available for this product.";
  };

  const getProductSideEffects = () => {
    // Simulated side effects based on prescription status
    if (currentProduct.prescription) {
      return "Common side effects may include nausea, drowsiness, dizziness, headache, dry mouth, or mild stomach pain. Serious side effects are rare when taken as directed. Contact your healthcare provider if you experience any severe or persistent side effects.";
    } else {
      return "When used as directed, this product has minimal side effects. Some people may experience mild stomach upset or allergic reactions. Discontinue use and consult a healthcare professional if any adverse reactions occur.";
    }
  };

  const getProductUsage = () => {
    // Simulated usage instructions
    if (currentProduct.prescription) {
      return "Take this medication exactly as prescribed by your doctor. Do not take in larger or smaller amounts or for longer than recommended. Follow all directions on your prescription label. If you have any questions, ask your doctor or pharmacist.";
    } else {
      return "For adults, take as needed according to the package instructions. Do not exceed the recommended dose. If symptoms persist for more than a few days, consult with a healthcare professional.";
    }
  };

  const renderContent = () => (
    <div className="flex flex-col h-full">
      <div className="flex justify-between items-start">
        <div className="mb-6">
          <Badge variant="outline" className="mb-2">
            {currentProduct.category}
          </Badge>
          {currentProduct.prescription && (
            <Badge variant="secondary" className="ml-2 mb-2 bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200">
              <AlertCircle className="mr-1 h-3 w-3" />
              Prescription Required
            </Badge>
          )}
          <h2 className="text-2xl font-bold mt-2">{currentProduct.title}</h2>
          <div className="flex items-center mt-1 mb-2">
            <div className="flex mr-2">
              <Star className="h-4 w-4 text-yellow-500 fill-yellow-500" />
              <Star className="h-4 w-4 text-yellow-500 fill-yellow-500" />
              <Star className="h-4 w-4 text-yellow-500 fill-yellow-500" />
              <Star className="h-4 w-4 text-yellow-500 fill-yellow-500" />
              <Star className="h-4 w-4 text-muted-foreground/30" />
            </div>
            <span className="text-sm text-muted-foreground">4.0 (24 reviews)</span>
          </div>
          <p className="text-2xl font-semibold">₦{currentProduct.price.toLocaleString()}</p>
        </div>
        
        <Button 
          variant="ghost" 
          size="icon" 
          className="rounded-full mt-2" 
          onClick={handleToggleFavorite}
        >
          <Heart className={`h-5 w-5 ${favorite ? 'fill-red-500 text-red-500' : ''}`} />
        </Button>
      </div>
      
      <div className="relative h-64 mb-6 rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-800">
        <img 
          src={currentProduct.image} 
          alt={currentProduct.title} 
          className="w-full h-full object-cover"
        />
      </div>
      
      <div className="flex space-x-2 border-b mb-4">
        <Button 
          variant="ghost" 
          className={`px-4 pb-2 ${activeTab === 'description' ? 'border-b-2 border-primary' : ''}`}
          onClick={() => setActiveTab('description')}
        >
          Description
        </Button>
        <Button 
          variant="ghost" 
          className={`px-4 pb-2 ${activeTab === 'effects' ? 'border-b-2 border-primary' : ''}`}
          onClick={() => setActiveTab('effects')}
        >
          Side Effects
        </Button>
        <Button 
          variant="ghost" 
          className={`px-4 pb-2 ${activeTab === 'usage' ? 'border-b-2 border-primary' : ''}`}
          onClick={() => setActiveTab('usage')}
        >
          Usage
        </Button>
      </div>
      
      <ScrollArea className="flex-1 pr-4">
        {activeTab === 'description' && (
          <div className="prose dark:prose-invert max-w-none">
            <p>{getProductDescription()}</p>
            
            <h3 className="text-lg font-semibold mt-4">Product Details</h3>
            <ul className="list-disc pl-5 mt-2 space-y-1">
              <li>Category: {currentProduct.category}</li>
              <li>SKU: {currentProduct.id}-PH</li>
              <li>Weight: 100g</li>
              <li>Expiry: 24 months from manufacture date</li>
              <li>Storage: Store in a cool, dry place below 25°C</li>
            </ul>
          </div>
        )}
        
        {activeTab === 'effects' && (
          <div className="prose dark:prose-invert max-w-none">
            <p>{getProductSideEffects()}</p>
            
            <h3 className="text-lg font-semibold mt-4">When to Seek Medical Help</h3>
            <p>Seek immediate medical attention if you experience:</p>
            <ul className="list-disc pl-5 mt-2 space-y-1">
              <li>Severe allergic reaction (rash, itching, swelling)</li>
              <li>Difficulty breathing</li>
              <li>Unusual bleeding or bruising</li>
              <li>Severe dizziness or fainting</li>
            </ul>
          </div>
        )}
        
        {activeTab === 'usage' && (
          <div className="prose dark:prose-invert max-w-none">
            <p>{getProductUsage()}</p>
            
            <h3 className="text-lg font-semibold mt-4">Dosage Information</h3>
            <ul className="list-disc pl-5 mt-2 space-y-1">
              <li>Adults: As directed by healthcare provider</li>
              <li>Children: Not recommended without medical supervision</li>
              <li>Elderly: May require dose adjustment</li>
            </ul>
            
            <h3 className="text-lg font-semibold mt-4">Interactions</h3>
            <p>May interact with other medications. Inform your doctor about all medications you are taking.</p>
          </div>
        )}
      </ScrollArea>
      
      <div className="flex items-center gap-4 mt-6 pt-4 border-t">
        <Button className="flex-1 rounded-full" onClick={handleAddToCart}>
          {currentProduct.prescription ? "Upload Prescription" : "Add to Tray"}
        </Button>
        <Button variant="outline" onClick={handleViewFullDetails} className="flex-1 rounded-full">
          View Full Details
        </Button>
      </div>
    </div>
  );

  if (isMobile) {
    return (
      <Drawer open={isOpen} onOpenChange={closeProductDetail}>
        <DrawerContent className="max-h-[90vh]">
          <div className="mx-auto w-full max-w-md p-6">
            {renderContent()}
          </div>
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Sheet open={isOpen} onOpenChange={closeProductDetail}>
      <SheetContent className="w-full sm:max-w-xl overflow-y-auto">
        {renderContent()}
      </SheetContent>
    </Sheet>
  );
}
